import { DateTime } from 'luxon'
import type { Reconciliation } from '@/reducers/admin/financialAccountsReducer'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'

export interface StartGap {
  priorClose: DateTime
  firstUncoveredDay: DateTime
  lastUncoveredDay: DateTime
  uncoveredDays: number
}

const utcDay = (value: string) =>
  DateTime.fromISO(value, { zone: 'utc' }).startOf('day')

/**
 * The days between a settled period's close and where this one opens, which no
 * reconciliation would cover. Mirrors the server rule. `excludeReconciliationId` is the
 * row this form writes, which cannot vouch for its own start.
 */
export const findStartGap = ({
  startDate,
  reconciliations,
  excludeReconciliationId,
}: {
  startDate: DateTime | null
  reconciliations: Reconciliation[]
  excludeReconciliationId?: number
}): StartGap | null => {
  if (!startDate?.isValid) return null

  const settled = reconciliations.filter(
    (recon) =>
      recon.status === 'complete' && recon.id !== excludeReconciliationId
  )
  const start = startDate.setZone('utc').startOf('day')
  const dayBefore = start.minus({ days: 1 })

  // A window overlapping a later settled period leaves this day covered, so key on
  // coverage rather than on distance to the nearest predecessor.
  const covered = settled.some(
    (recon) =>
      utcDay(recon.startingBalanceDate) <= dayBefore &&
      utcDay(recon.endingBalanceDate) >= dayBefore
  )
  if (covered) return null

  const priorClose = settled
    .map((recon) => utcDay(recon.endingBalanceDate))
    .filter((close) => close < dayBefore)
    .reduce<DateTime | null>(
      (latest, close) => (latest === null || close > latest ? close : latest),
      null
    )
  if (!priorClose) return null

  return {
    priorClose,
    firstUncoveredDay: priorClose.plus({ days: 1 }),
    lastUncoveredDay: dayBefore,
    uncoveredDays: dayBefore.diff(priorClose, 'days').days,
  }
}

export type ClosingBasis =
  | { kind: 'monthEnd' }
  | { kind: 'billingDay'; day: number }

const isLastDayOfMonth = (close: DateTime) => close.day === close.daysInMonth

/** How many recent closes to read a basis from. A year covers every short month. */
const BASIS_SAMPLE = 12

/**
 * Which basis an account closes on, read from several closes rather than one. A single
 * close cannot distinguish the two: February clamps a 29th or 30th billing day to the
 * 28th, which on its own is indistinguishable from a true month-end account.
 *
 * Month-end means the last day of *every* month the account closes in. Otherwise the
 * latest close carries the billing day, so an account that moved onto a new cycle keeps
 * the new one. Only when that latest close is itself a month's last day, and so may be a
 * longer billing day clamped by a short month, is the day recovered from the window.
 */
export const closingBasis = (closes: DateTime[]): ClosingBasis | null => {
  const recent = closes
    .filter((close) => close.isValid)
    .sort((a, b) => b.toMillis() - a.toMillis())
    .slice(0, BASIS_SAMPLE)
  const latest = recent[0]
  if (!latest) return null
  if (recent.every(isLastDayOfMonth)) return { kind: 'monthEnd' }
  return {
    kind: 'billingDay',
    day: isLastDayOfMonth(latest)
      ? Math.max(...recent.map((close) => close.day))
      : latest.day,
  }
}

/**
 * The close to seed the form with, which is whichever basis the account already closes on
 * applied to the month being reconciled. Month end and statement cycle are both valid;
 * what damages an account is carrying two of them, because adjacent windows on different
 * bases overlap. Month end is the fallback only for an account with no history to read.
 *
 * Derived from the target month rather than advanced from the last close, so a month
 * nobody reconciled cannot move the account onto the other basis. A future close is not
 * offered: the statement does not exist yet.
 */
export const defaultCloseDate = ({
  closes,
  reportMonth,
  reportEnd,
  today,
}: {
  closes: DateTime[]
  reportMonth: string
  reportEnd: DateTime
  today: DateTime
}) => {
  const basis = closingBasis(closes)
  if (!basis) return reportEnd

  const monthStart = DateTime.fromFormat(
    reportMonth,
    DATE_FORMATS_LUXON.YEAR_MONTH,
    { zone: 'utc' }
  )
  if (!monthStart.isValid) return reportEnd

  const candidate =
    basis.kind === 'monthEnd'
      ? monthStart.endOf('month').startOf('day')
      : monthStart.set({ day: Math.min(basis.day, monthStart.daysInMonth) })

  return candidate <= today ? candidate : reportEnd
}
